import type React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { onlyNumbers } from '../../../../helpers/common';
import { encrypt } from '../../../../helpers/crypt/crypt';
import {
  getCreditCardLabel,
  updateHolderName,
  updateMaskCVV,
  updateMaskCard,
  validateCVV,
} from '../../../../helpers/masks/cardNumber';
import { updateMask as cpfCnpjMask } from '../../../../helpers/masks/cpfCnpj';
import { updateMask as expirationDateMask } from '../../../../helpers/masks/expirationDate';
import validators from '../../../../helpers/validators';
import useForm from '../../../../hooks/useForm';
import type CardClient from '../../../../model/CardClient';
import type CardClientCrypt from '../../../../model/CardClientCrypt';
import type CardDb from '../../../../model/CardDb';
import { setLoading } from '../../../../redux/loading/loadingSlice';
import { api } from '../../../../services/api';
import { FormInputNameCreditCard } from '../../types';
import { CreditCardPaymentAddUI } from './ui';
import type { Address } from 'src/model/Address';

export const CreditCardPaymentAddScreen = () => {
  const dispatch = useDispatch();
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');
  const [showNewAddressForm, setShowNewAddressForm] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [cards, setCard] = useState<CardDb[]>([]);

  const {
    formData: newAddressData,
    formErrors: newAddressErrors,
    onChangeFormInput: onChangeNewAddressInput,
    isFormValid: isNewAddressValid,
    resetForm: resetNewAddressForm,
  } = useForm({
    initialData: {
      street: '',
      number: '',
      zipcode: '',
      city: '',
      state: '',
      district: '',
      complement: '',
      country: 'Brasil',
      main: 'false',
    },
    validators: {
      street: [validators.required],
      number: [validators.required],
      district: [validators.required],
      city: [validators.required],
      state: [validators.required],
      zipcode: [validators.required],
      country: [validators.required],
    },
  });

  const getAddresses = async (): Promise<void> => {
    try {
      const response = await api.get<Address[]>('/user/address');
      setAddresses(response.data);
    } catch (error) {
      toast.error('Ocorreu um erro ao carregar os endereços');
    }
  };

  const getCards = async (): Promise<void> => {
    dispatch(setLoading(true));
    try {
      const response = await api.get<CardDb[]>('/client-card/find-all');
      setCard(response.data);
    } catch (error) {
      toast.error('Ocorreu um erro ao carregar os cartões');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const {
    formData: formDataAddCreditCard,
    formErrors: formErrorsAddCreditCard,
    setErrors: setErrorsAddCreditCard,
    onChangeFormInput: onChangeFormInputAddCreditCard,
    isFormValid: isFormValidAddCreditCard,
    resetForm: resetFormAddCreditCard,
  } = useForm({
    initialData: {
      number: '',
      date: '',
      cvv: '',
      name: '',
      document: '',
      label: '',
    },
    validators: {
      number: [validators.required, validators.cardNumber],
      date: [validators.required, validators.cardExpirationDate],
      cvv: [validators.required, validators.cvv],
      name: [validators.required],
      document: [validators.required, validators.cpforcnpj],
    },
    formatters: {
      number: updateMaskCard,
      date: expirationDateMask,
      cvv: updateMaskCVV,
      name: updateHolderName,
      document: cpfCnpjMask,
    },
  });

  const handleChangeCEP = async (cep: string) => {
    if (cep.replace(/\D/g, '').length === 8) {
      try {
        const response = await fetch(
          `https://viacep.com.br/ws/${cep.replace(/\D/g, '')}/json/`
        );
        const data = await response.json();

        if (!data.erro) {
          onChangeNewAddressInput('street')(data.logradouro || '');
          onChangeNewAddressInput('district')(data.bairro || '');
          onChangeNewAddressInput('city')(data.localidade || '');
          onChangeNewAddressInput('state')(data.uf || '');
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  const onSubmitAddCreditCard = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!selectedAddressId && !showNewAddressForm) {
      toast.error('Selecione um endereço de cobrança');
      return;
    }

    if (showNewAddressForm && !isNewAddressValid()) {
      toast.error('Preencha todos os campos obrigatórios do endereço');
      return;
    }

    if (isFormValidAddCreditCard()) {
      if (
        validateCVV(
          formDataAddCreditCard[FormInputNameCreditCard.number],
          formDataAddCreditCard[FormInputNameCreditCard.cvv]
        )
      ) {
        try {
          dispatch(setLoading(true));
          const expDate =
            formDataAddCreditCard[FormInputNameCreditCard.date].split('/');

          let addressId = selectedAddressId;

          // Se está criando um novo endereço
          if (showNewAddressForm) {
            const addressPayload = {
              street: newAddressData.street,
              number: newAddressData.number,
              district: newAddressData.district,
              city: newAddressData.city,
              state: newAddressData.state,
              zipcode: newAddressData.zipcode,
              complement: newAddressData.complement,
              country: newAddressData.country,
              main: newAddressData.main === 'true',
            };

            const addressResponse = await api.post(
              '/user/address',
              addressPayload
            );
            addressId = addressResponse.data.id;

            // Atualiza a lista de endereços
            await getAddresses();
          }

          const cardData: any = {
            holder: formDataAddCreditCard[FormInputNameCreditCard.name],
            number: onlyNumbers(
              formDataAddCreditCard[FormInputNameCreditCard.number]
            ),
            expMonth: expDate[0],
            expYear: `20${expDate[1]}`,
            securityCode: formDataAddCreditCard[FormInputNameCreditCard.cvv],
            document: onlyNumbers(
              formDataAddCreditCard[FormInputNameCreditCard.document]
            ),
            brand: String(
              getCreditCardLabel(
                formDataAddCreditCard[FormInputNameCreditCard.number]
              )
            ).toLowerCase(),
            //label: formDataAddCreditCard['label'] || 'Meu Cartão',
            cardType: String(
              getCreditCardLabel(
                onlyNumbers(
                  formDataAddCreditCard[FormInputNameCreditCard.number]
                )
              )
            )
              .toLowerCase()
              .replace('_', ''),
          };

          if (addressId) {
            cardData.addressId = addressId;
          }

          const encrypted = encrypt(JSON.stringify(cardData));

          const cardClientCrypt: CardClientCrypt = {
            data: encrypted,
          };

          const responseCardClient = await api.post<CardClient>(
            '/client-card',
            cardClientCrypt
          );

          if (responseCardClient.status === 201) {
            getCards();
            resetFormAddCreditCard();
            resetNewAddressForm();
            setSelectedAddressId('');
            setShowNewAddressForm(false);
            toast.success('Cartão adicionado com sucesso!');
          }
        } catch (error: any) {
          setErrorsAddCreditCard({
            ['document']: ['Algo deu errado'],
          });
        } finally {
          dispatch(setLoading(false));
        }
      } else {
        toast.error('Código de segurança inválido!');
        resetFormAddCreditCard();
        dispatch(setLoading(false));
      }
    }
  };

  const handleOnDeleteCard = async (id: string): Promise<void> => {
    try {
      if (id) {
        dispatch(setLoading(true));
        await api.get(`/client-card/delete/${id}`);
        getCards();
        toast.success('Cartão removido com sucesso!');
      } else {
        toast.error('Nenhum cartão selecionado');
      }
    } catch (error: any) {
      dispatch(setLoading(false));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAddressSelection = (addressId: string) => {
    setSelectedAddressId(addressId);
    if (addressId === 'new') {
      setShowNewAddressForm(true);
    } else {
      setShowNewAddressForm(false);
      resetNewAddressForm();
    }
  };

  useEffect(() => {
    getCards();
    getAddresses();
  }, []);

  return (
    <CreditCardPaymentAddUI
      formData={formDataAddCreditCard}
      formErrors={formErrorsAddCreditCard}
      onChangeFormInput={onChangeFormInputAddCreditCard}
      onSubmitRegister={onSubmitAddCreditCard}
      cards={cards}
      onDeleteCard={handleOnDeleteCard}
      addresses={addresses}
      selectedAddressId={selectedAddressId}
      onAddressSelection={handleAddressSelection}
      showNewAddressForm={showNewAddressForm}
      newAddressData={newAddressData}
      newAddressErrors={newAddressErrors}
      onChangeNewAddressInput={onChangeNewAddressInput}
      onChangeCEP={handleChangeCEP}
    />
  );
};
